<template>
  <div class="slideover-wrap" :style="{ zIndex: zIndex }">
    <div
      class="slideover-backdrop"
      :style="{ zIndex: zIndex }"
      @click="handleBackdropClick"
    ></div>
    <aside
      class="slideover-panel"
      :class="widthClass"
      :style="{ zIndex: zIndex + 1 }"
      role="dialog"
      aria-modal="true"
    >
      <div class="slideover-surface bg-body p-2">
        <div class="slideover-header bg-body border-bottom">
          <div>
            <h3 class="slideover-title mb-0">{{ title }}</h3>
            <p v-if="description" class="slideover-description mb-0 text-muted">
              {{ description }}
            </p>
          </div>
          <div class="block-options">
            <slot name="header-actions"></slot>
            <button
              type="button"
              class="btn-block-option"
              aria-label="Close"
              @click="close"
            >
              <i class="fa fa-fw fa-times"></i>
            </button>
          </div>
        </div>

        <div class="slideover-body bg-body">
          <slot></slot>
        </div>

        <div
          v-if="$slots.footer"
          class="slideover-footer bg-body-light border-top"
        >
          <slot name="footer"></slot>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: "",
  },
  widthClass: {
    type: String,
    default: "slideover-xl",
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  zIndex: {
    type: Number,
    default: 1050,
  },
});

const emit = defineEmits(["close"]);

function close() {
  emit("close");
}

function handleBackdropClick() {
  if (!props.closeOnBackdrop) {
    return;
  }

  close();
}
</script>

<style scoped>
.slideover-wrap {
  position: fixed;
  inset: 0;
}

.slideover-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}

.slideover-panel {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  max-width: 100vw;
  animation: slideoverIn 0.22s ease-out;
}

.slideover-surface {
  display: flex;
  height: 100%;
  flex-direction: column;
  color: inherit;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: -0.75rem 0 2rem rgba(0, 0, 0, 0.18);
}

.slideover-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

.slideover-title {
  font-size: 1rem;
}

.slideover-description {
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

.slideover-body {
  flex: 1 1 auto;
  overflow: auto;
}

.slideover-footer {
  padding: 1rem 1.25rem;
}

.slideover-sm {
  width: min(24rem, 100vw);
}

.slideover-md {
  width: min(30rem, 100vw);
}

.slideover-lg {
  width: min(36rem, 100vw);
}

.slideover-xl {
  width: min(44rem, 100vw);
}

:global(.dark-mode) .slideover-surface {
  border-left-color: rgba(255, 255, 255, 0.08);
  box-shadow: -0.75rem 0 2rem rgba(0, 0, 0, 0.4);
}

@keyframes slideoverIn {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }
}
</style>
